import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useSession } from 'next-auth/react';
import { Palette, ColorLibraryData, Color, Season } from '../../types/ColorLibraryModel';
import {
  loadColorLibrary,
  loadUserPalettes,
  savePalette,
  deletePalette as deletePaletteAPI,
} from '../../api/color-library';

import ColorLibraryAll from './ColorLibraryAll';
import MyPalettes from './MyPalettes';
import { AppInsightsContext, MetadataContext, TrackEventKey, TrackEventName, TrackEventType, UserContext } from '..';
import { useRouter } from 'next/router';

const defaultEffects = ['Matte', 'Metallic', 'Lustre', 'Chrome'];
const defaultSeasons = [
  { categoryCodeName: 'spring', categoryDisplayName: 'Spring' },
  { categoryCodeName: 'summer', categoryDisplayName: 'Summer' },
  { categoryCodeName: 'autumn', categoryDisplayName: 'Autumn' },
  { categoryCodeName: 'winter', categoryDisplayName: 'Winter' },
];

interface ColorLibraryProps {
  data?: ColorLibraryData & {
    colours?: Color[];
    effects?: string[];
    seasons?: Season[];
  };
}

interface TabItem {
  tabName: string;
  component: React.ComponentType<any>;
  props: any;
}

const ColorLibrary: React.FC<ColorLibraryProps> = ({ data }) => {
  const defaultData = {
    backgroundColor: '#ffffff',
    palettes: [] as Palette[],
    colours: [] as Color[],
    effects: [] as string[],
    seasons: [] as Season[],
  };
  const dataObj = data || defaultData;
  const { data: session } = useSession();
  const [activeTab, setActiveTab] = useState(0);
  const [palettes, setPalettes] = useState<Palette[]>([]);
  const isLoggedIn = !!session;
  const [windowWidth, setWindowWidth] = useState(1024);

  const appInsights = useContext(AppInsightsContext);
  const user = useContext(UserContext);
  const { pageId } = useContext(MetadataContext);
  const router = useRouter();

  const loadPalettes = useCallback(async () => {
    if (!isLoggedIn) return;

    try {
      const { result } = await loadUserPalettes();
      setPalettes(result || []);
    } catch (error) {
      console.error('Error loading palettes:', error);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    loadPalettes();
  }, [loadPalettes]);

  const updatePalette = useCallback(
    async (updatedPalette: Palette) => {
      try {
        await savePalette(updatedPalette);
        loadPalettes();
      } catch (error) {
        console.error('Error updating palette:', error);
      }
    },
    [loadPalettes],
  );

  const deletePalette = useCallback(
    async (id: string) => {
      try {
        await deletePaletteAPI(id);
        loadPalettes();
      } catch (error) {
        console.error('Error deleting palette:', error);
      }
    },
    [loadPalettes],
  );

  const downloadPalette = useCallback(
    (palette: Palette) => {
      appInsights.trackCustomEvent(TrackEventName.REPORT, TrackEventKey.COLOUR_PALETTE, TrackEventType.DOWNLOAD, user, {
        pageId,
        pagePath: router.asPath,
      });
      try {
        if (!palette.colours || palette.colours.length === 0) {
          console.warn('No colours in palette to download');
          return;
        }

        const form = document.getElementById('stDownloadPaletteForm') as HTMLFormElement;
        if (form) {
          const nameInput = form.querySelector('input[name="name"]') as HTMLInputElement;
          const colourIdsInput = form.querySelector('input[name="colourIds"]') as HTMLInputElement;

          if (nameInput && colourIdsInput) {
            nameInput.value = palette.name;
            colourIdsInput.value = palette.colours.map((c) => c.id).join(',');
            form.submit();
          }
        }
      } catch (error) {
        console.error('Error downloading palette:', error);
      }
    },
    [appInsights, pageId, router.asPath, user],
  );

  const downloadColor = useCallback(
    (colors: Color[]) => {
      appInsights.trackCustomEvent(TrackEventName.REPORT, TrackEventKey.COLOUR_CHIP, TrackEventType.DOWNLOAD, user, {
        pageId,
        pagePath: router.asPath,
      });
      try {
        const form = document.getElementById('stDownloadPaletteForm') as HTMLFormElement;
        if (form) {
          const nameInput = form.querySelector('input[name="name"]') as HTMLInputElement;
          const colourIdsInput = form.querySelector('input[name="colourIds"]') as HTMLInputElement;

          if (nameInput && colourIdsInput) {
            nameInput.value = colors.length === 1 ? colors[0].displayName : 'colours';
            colourIdsInput.value = colors.map((c) => c.id).join(',');
            form.submit();
          }
        }
      } catch (error) {
        console.error('Error downloading colors:', error);
      }
    },
    [appInsights, pageId, router.asPath, user],
  );

  // Load colors from API or use sample data
  const [apiColors, setApiColors] = useState<Color[]>([]);

  const loadColors = useCallback(async () => {
    try {
      const result = await loadColorLibrary();
      setApiColors(result.colours || []);
    } catch (error) {
      console.error('Error loading colors:', error);
      // Fallback to sample data if API fails
      setApiColors([]);
    }
  }, []);

  useEffect(() => {
    loadColors();
  }, [loadColors]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sampleColors = useMemo(
    () => (apiColors.length > 0 ? apiColors : dataObj.colours || []),
    [apiColors, dataObj.colours],
  );

  const libraryData = useMemo(
    () => ({
      backgroundColor: dataObj.backgroundColor,
      palettes,
      colours: sampleColors,
      effects: dataObj.effects || defaultEffects,
      seasons: dataObj.seasons || defaultSeasons,
    }),
    [dataObj.backgroundColor, palettes, sampleColors, dataObj.effects, dataObj.seasons],
  );

  const paletteData = useMemo(() => ({ ...dataObj, palettes }), [dataObj, palettes]);

  const tabs: TabItem[] = useMemo(() => {
    const tabsArray: TabItem[] = [
      {
        tabName: 'Library',
        component: ColorLibraryAll as React.ComponentType<any>,
        props: {
          data: libraryData,
          onUpdatePalette: updatePalette,
          onDownloadColor: downloadColor,
          isActive: activeTab === 0,
        },
      },
    ];

    if (isLoggedIn) {
      tabsArray.push({
        tabName: `My Palettes (${palettes.length})`,
        component: MyPalettes as React.ComponentType<any>,
        props: {
          data: paletteData,
          onUpdatePalette: updatePalette,
          onDeletePalette: deletePalette,
          onDownloadPalette: downloadPalette,
          onDownloadColor: downloadColor,
        },
      });
    }

    return tabsArray;
  }, [
    libraryData,
    paletteData,
    palettes.length,
    isLoggedIn,
    updatePalette,
    deletePalette,
    downloadPalette,
    downloadColor,
    activeTab,
  ]);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  return (
    <div className="container">
      {/* Tab Navigation */}
      <div className="relative mb-4 sm:mb-6">
        <div className="flex border-b border-gray-200">
          {/* Mobile/Tablet Indicator */}
          <div
            className={`absolute bottom-0 z-10 h-0.5 bg-black transition-all duration-300 ${isMobile || isTablet ? 'block' : 'hidden'}`}
            style={{
              left: activeTab * (tabs.length === 1 ? 100 : 50) + '%',
              width: tabs.length === 1 ? '100%' : '50%',
            }}
          />
          {/* Desktop Indicator */}
          <div
            className={`absolute bottom-0 z-10 h-0.5 w-1/2 bg-black transition-all duration-300 ${!isMobile && !isTablet ? 'block' : 'hidden'}`}
            style={{
              left: activeTab === 0 ? '0px' : '50%',
            }}
          />
          {tabs.map((tab, index) => (
            <div
              key={index}
              tabIndex={0}
              aria-selected={activeTab === index}
              role="tab"
              className={`relative flex cursor-pointer items-center justify-center transition-all duration-200 hover:bg-gray-300 ${
                tabs.length === 1 ? 'w-full' : 'w-1/2'
              } ${
                isMobile
                  ? 'h-10 px-2 py-2 text-xs'
                  : isTablet
                    ? 'h-11 px-4 py-2 text-sm'
                    : 'h-12 px-6 py-3 text-sm lg:text-base'
              } font-medium ${
                activeTab === index
                  ? 'v-tab--active text-black'
                  : 'v-tab text-gray-600 hover:bg-gray-50 hover:text-gray-800'
              }`}
              onClick={() => setActiveTab(index)}
            >
              <span className="font-soehne truncate text-lg font-semibold text-black capitalize">{tab.tabName}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {tabs.map((tab, index) => {
          const Component = tab.component;
          return (
            <div key={index} className={activeTab === index ? '' : 'hidden'}>
              <Component {...tab.props} />
            </div>
          );
        })}
      </div>

      {/* Hidden form for downloads */}
      <form
        id="stDownloadPaletteForm"
        method="POST"
        target="_blank"
        action={`${process.env.NEXT_PUBLIC_API_URL || 'https://uat-web-api.testing-stylus.com'}/umbraco/Api/ColourPaletteRenderer/RenderPalette2`}
        className="hidden"
      >
        <input type="hidden" name="name" />
        <input type="hidden" name="colourIds" />
      </form>
    </div>
  );
};

export default ColorLibrary;
